import {
  ConsolidatedCoinData,
  LanguageNameSpaces,
  MarginTypes,
  SetLeverageRequest,
  SetRiskLimitRequest,
} from "@shared/types";
import Modal from "common/Modal/Modal";
import React, { useEffect, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import Select from "common/Select/Select.tsx";
import TextWithIcon from "common/Text/TextWithIcon";
import {
  ICON_TYPES,
  MAX_LEVERAGE_CROSS_EXCHANGES,
} from "@shared/common/constants.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import HighlightedText from "common/Text/HighlightedText";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import { displayResultMessage, openMessage } from "utils/misc";
import { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import Button from "common/Buttons/Button";
import styled from "styled-components";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

interface ChangeLeverageModalProps {
  coinData?: ConsolidatedCoinData;
  closeModal: () => void;
  visible: boolean;
}

const ChangeLeverageModal = ({
  coinData,
  closeModal,
  visible,
}: ChangeLeverageModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const appTheme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isWhitelabel } = useBranding();
  const [multiplier, setMultiplier] = useState<number>();
  const [marginType, setMarginType] = useState<MarginTypes>();
  const [riskLimitID, setRiskLimitID] = useState<number>();
  const exchange = coinData?.exchanges[0];
  const accountName = coinData?.clients[0];
  const accountId = coinData?.client_id;
  const { data: leverageOptionsData, ...getLeverageOptionsStatus } =
    sharedLib.sharedApi.portfolio.useGetLeverageOptionsQuery(
      accountId && visible ? { account_id: accountId } : skipToken
    );
  const fetchRiskLimitOptions = accountId && coinData?.symbol && visible;
  const { data: riskLimitOptionsData, ...getRiskLimitOptionsStatus } =
    sharedLib.sharedApi.portfolio.useGetRiskLimitOptionsQuery(
      fetchRiskLimitOptions
        ? { account_id: accountId, symbol: coinData?.symbol }
        : skipToken
    );
  const fetchRiskLimit =
    fetchRiskLimitOptions &&
    riskLimitOptionsData &&
    !getRiskLimitOptionsStatus.isFetching;
  const { data: rawOriginalRiskLimitID, ...getRiskLimitStatus } =
    sharedLib.sharedApi.portfolio.useGetRiskLimitQuery(
      fetchRiskLimit
        ? {
            account_id: accountId,
            symbol: coinData?.symbol,
            reverse: !!coinData.is_short,
          }
        : skipToken,
      { refetchOnMountOrArgChange: true }
    );
  const originalRiskLimitID = fetchRiskLimit
    ? rawOriginalRiskLimitID
    : undefined;

  const [setLeverage, setLeverageStatus] =
    sharedLib.sharedApi.portfolio.useSetLeverageMutation();
  const [setRiskLimitMutation, setRiskLimitStatus] =
    sharedLib.sharedApi.portfolio.useSetRiskLimitMutation();
  const [getPortfolioData] =
    sharedLib.sharedApi.portfolio.useLazyGetPortfolioDataQuery();
  const { getMainCoin } = useSymbolsMetadata();
  const mainCoin = getMainCoin(coinData?.symbol, exchange);
  const riskLimitData = useMemo(() => {
    if (riskLimitID && riskLimitOptionsData)
      return riskLimitOptionsData.find((option) => option.id === riskLimitID);
  }, [riskLimitID]);

  const getOptionsAndDefaultValue = (
    optionsName: "Multiplier" | "Margin Type"
  ) =>
    (coinData &&
      leverageOptionsData?.options[optionsName]?.[0]?.[coinData?.symbol]) ||
    [];

  const [allMultiplierOptions] = getOptionsAndDefaultValue("Multiplier");

  const multiplierOptions = riskLimitData
    ? allMultiplierOptions?.filter(
        (option) => option.value <= riskLimitData.max_leverage
      )
    : allMultiplierOptions;
  const [marginTypeOptions] = getOptionsAndDefaultValue("Margin Type");
  const isMaxLeverageCrossExchange =
    exchange && MAX_LEVERAGE_CROSS_EXCHANGES.includes(exchange);
  const parsedMultiplier = (multiplierOptions?.length && multiplier) || null;
  const parsedMarginType = (marginTypeOptions?.length && marginType) || null;
  const parsedRiskLimit = (riskLimitOptionsData?.length && riskLimitID) || null;
  const leverageChanged =
    parsedMarginType !== coinData?.margin_type ||
    parsedMultiplier !== coinData?.leverage;
  const riskLimitChanged = parsedRiskLimit !== originalRiskLimitID;
  const anyValueChanged = leverageChanged || riskLimitChanged;

  const resetData = () => {
    setMultiplier(undefined);
    setMarginType(undefined);
    setRiskLimitID(undefined);
  };

  useEffect(() => {
    if (!visible) resetData();
  }, [visible]);

  useEffect(() => {
    if (leverageOptionsData && visible) {
      let marginType = coinData?.margin_type;
      let multiplier = coinData?.leverage;
      if (
        isMaxLeverageCrossExchange &&
        marginType === MarginTypes.CROSS &&
        multiplierOptions?.length
      ) {
        multiplier = multiplierOptions[multiplierOptions.length - 1].value;
      }
      setMarginType(marginType);
      setMultiplier(multiplier);
    }
  }, [leverageOptionsData, visible]);

  useEffect(() => {
    if (originalRiskLimitID && visible) {
      setRiskLimitID(originalRiskLimitID);
    }
  }, [originalRiskLimitID, visible]);

  const getFieldTitle = (title: string) => {
    return (
      <Text $size={typography.fontSizes.S} style={{ marginBottom: 4 }}>
        {title}
      </Text>
    );
  };

  const getRiskLimitMultiplierWarning = () => {
    if (
      riskLimitData &&
      multiplier &&
      multiplier > riskLimitData.max_leverage
    ) {
      return (
        <Text type="danger" $size="S" style={{ marginTop: 6 }}>
          The selected multiplier is greater than the maximum allowed for this
          risk limit ({riskLimitData.max_leverage}x).
        </Text>
      );
    }
  };

  const riskLimitWarning = getRiskLimitMultiplierWarning();
  const isValid =
    coinData && accountId && (marginType || multiplier) && !riskLimitWarning;

  const handleSetRiskLimit = async () => {
    if (riskLimitID && riskLimitChanged && isValid) {
      const args: SetRiskLimitRequest = {
        account_id: accountId,
        symbol: coinData.symbol,
        reverse: !!coinData.is_short,
        risk_id: riskLimitID,
      };
      await displayResultMessage(
        setRiskLimitMutation(args).unwrap(),
        undefined,
        t("errors.update_risk_limit")
      );
    }
  };

  const handleSetLeverage = async () => {
    if (isValid && leverageChanged) {
      const args: SetLeverageRequest = {
        account_id: accountId,
        symbol: coinData.symbol,
        reverse: !!coinData.is_short,
        margin_type: parsedMarginType,
        multiplier: parsedMultiplier,
      };
      await displayResultMessage(
        setLeverage(args).unwrap(),
        undefined,
        t("errors.update_leverage")
      );
    }
  };

  const getSymbolDisplay = () => {
    const symbol = coinData?.symbol;
    const symbolDisplay = (
      <TextWithIcon
        coinOrExchangeName={symbol}
        iconType={ICON_TYPES.COIN}
        text={symbol}
      />
    );
    return (
      <div>
        {getFieldTitle(t("positions.symbol"))}
        <Select<string>
          value={symbol}
          disabled={true}
          hideArrow={true}
          style={{ width: "100%" }}
        >
          <Select.Option key={symbol} value={symbol} label={symbolDisplay}>
            {symbolDisplay}
          </Select.Option>
        </Select>
      </div>
    );
  };

  const getAccountSelector = () => {
    const accountDisplay = (
      <TextWithIcon
        coinOrExchangeName={exchange}
        iconType={ICON_TYPES.EXCHANGE}
        text={accountName}
      />
    );
    return (
      <div>
        {getFieldTitle(t("positions.account"))}
        <Select<number>
          value={accountId}
          popupMatchSelectWidth={false}
          disabled={true}
          hideArrow={true}
          style={{ width: "100%" }}
        >
          <Select.Option
            key={accountId}
            value={accountId}
            label={accountDisplay}
          >
            {accountDisplay}
          </Select.Option>
        </Select>
      </div>
    );
  };

  const getRiskLimitSelector = () => {
    if (!riskLimitData) return undefined;
    return (
      <div>
        {getFieldTitle(t("positions.risk_limit", { mainCoin }))}
        <Select
          style={{ width: "100%" }}
          value={riskLimitID}
          onChange={setRiskLimitID}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
        >
          {riskLimitOptionsData?.map((option) => {
            return (
              <Select.Option
                value={option.id}
                key={option.id}
                label={option.limit}
              >
                {option.limit}
              </Select.Option>
            );
          })}
        </Select>
      </div>
    );
  };

  const onMarginTypeChange = (value: MarginTypes) => {
    setMarginType(value);
    if (
      multiplierOptions?.length &&
      value === MarginTypes.CROSS &&
      isMaxLeverageCrossExchange
    ) {
      setMultiplier(multiplierOptions[multiplierOptions.length - 1].value);
    }
  };

  const getMarginTypeSelector = () => {
    if (!marginTypeOptions?.length) return null;
    return (
      <div>
        {getFieldTitle(t("positions.margin_type"))}
        <Select
          style={{ width: "100%" }}
          value={marginType}
          onChange={onMarginTypeChange}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
        >
          {marginTypeOptions?.map((option) => {
            return (
              <Select.Option
                value={option.value}
                key={option.value}
                label={option.name}
              >
                {option.name}
              </Select.Option>
            );
          })}
        </Select>
      </div>
    );
  };

  const getMultiplierSelector = () => {
    if (!multiplierOptions?.length) return null;
    const disabled =
      marginType === MarginTypes.CROSS && isMaxLeverageCrossExchange;
    return (
      <div>
        {getFieldTitle(t("positions.multiplier"))}
        <Select
          style={{ width: "100%" }}
          value={multiplier || 1}
          onChange={setMultiplier}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
          disabled={!!disabled}
        >
          {multiplierOptions?.map((option) => {
            return (
              <Select.Option
                value={option.value}
                key={option.value}
                label={option.name}
              >
                {option.name}
              </Select.Option>
            );
          })}
        </Select>
      </div>
    );
  };

  const onFinish = () => {
    openMessage("Successfully updated risk limit and leverage");
    getPortfolioData({ use_cache: false });
    closeModal();
  };

  const handleSubmit = async () => {
    if (anyValueChanged) {
      if (
        parsedMultiplier &&
        coinData &&
        parsedMultiplier > coinData?.leverage
      ) {
        await handleSetRiskLimit().then(handleSetLeverage).then(onFinish);
      } else {
        await handleSetLeverage().then(handleSetRiskLimit).then(onFinish);
      }
    } else {
      openMessage("Please change one of the values to proceed", "warning");
    }
  };

  const isLoading =
    getLeverageOptionsStatus.isLoading ||
    getRiskLimitOptionsStatus.isLoading ||
    getRiskLimitStatus.isLoading;
  const isActionFetching =
    setLeverageStatus.isLoading || setRiskLimitStatus.isLoading;

  const footer = (
    <Flex $justifyContent="end" style={{ columnGap: 8 }}>
      <Button onClick={closeModal} disabled={isActionFetching}>
        Cancel
      </Button>
      <StyledButton
        type="primary"
        loading={isActionFetching}
        $disabled={!isValid || !anyValueChanged}
        onClick={handleSubmit}
      >
        Ok
      </StyledButton>
    </Flex>
  );

  return (
    <Modal
      open={visible}
      onCancel={closeModal}
      title="Update Position Leverage"
      onOk={handleSubmit}
      okButtonProps={{
        loading: isActionFetching,
        disabled: !isValid,
      }}
      destroyOnHidden={true}
      footer={footer}
      width={580}
      styles={{
        body: {
          padding: isBottomNavigator ? 12 : 24,
        },
      }}
    >
      <LoadingSpinnerOverlay
        isLoading={
          isLoading || isActionFetching || getRiskLimitStatus.isFetching
        }
        hideContentWhileLoading={isLoading}
        backgroundColor={appTheme.colors.cardBgColor}
        wrap={true}
      >
        <EqualColumnsGrid numColumns={2}>
          {getAccountSelector()}
          {getSymbolDisplay()}
        </EqualColumnsGrid>
        <EqualColumnsGrid
          numColumns={riskLimitData ? 3 : 2}
          style={{ marginTop: 16 }}
        >
          {getRiskLimitSelector()}
          {getMarginTypeSelector()}
          {getMultiplierSelector()}
        </EqualColumnsGrid>
        {riskLimitWarning}
        <HighlightedText style={{ marginTop: 16 }}>
          {`This change will affect your current position on the exchange but your leverage settings will not be up-to-date for trades when viewed on ${
            isWhitelabel ? "the platform" : "Cornix"
          }`}
        </HighlightedText>
      </LoadingSpinnerOverlay>
    </Modal>
  );
};

export default ChangeLeverageModal;

const StyledButton = styled(Button)<{ $disabled?: boolean }>`
  ${(p) => p.$disabled && `opacity: 0.4`}
`;
